import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { TreeView, TreeItem } from '@mui/lab';
import { Checkbox, Grid, FormControlLabel } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';

import IPipelineData from '../../types/IPipelineData';
import IPipelineObject from '../../types/IPipelineObject';
import WsTypography, { WsTypoVariants, WsTypoColors } from '../WsTypography/WsTypography';

export interface IRecruiterActionAndResults {
  actions: string[];
  results: string[];
}
interface IRecruiterActivity {
  candidateInteractionMessagingPipeline: IPipelineObject;
  selectedActions?: string[];
  candidateInteractionResultPipeline: IPipelineObject;
  selectedResults?: string[];
  onInputChange(args: IRecruiterActionAndResults): void;
}

const RecruiterActivity: FC<IRecruiterActivity> = ({
  candidateInteractionMessagingPipeline,
  selectedActions = [],
  candidateInteractionResultPipeline,
  selectedResults = [],
  onInputChange,
}) => {
  const [intializeState, setIntializeState] = useState(false);
  const [actions, setActions] = useState<string[]>([]);
  const [results, setResults] = useState<string[]>([]);
  const [expansionNodeTracker, setExpansionNodeTracker] = useState<string[]>([]);

  useEffect(() => {
    if (!intializeState) {
      setActions(selectedActions);
      setResults(selectedResults);
      onInputChange({
        actions: selectedActions,
        results: selectedResults,
      });
    }
  }, [intializeState, onInputChange, selectedActions, selectedResults]);

  function* generator(i: number) {
    while (true) yield i++ * 3;
  }

  const gen = generator(3);

  const setActionsOrResults = (
    field: string,
    value: string,
    nextValue: string,
    nextNextVal: string,
    recruiterAction: IPipelineData
  ) => {
    let tempActions = [...actions];
    let tempResults = [...results];
    if (field == 'actions') {
      if (tempActions.includes(value)) {
        if (recruiterAction.name === value) {
          tempActions = tempActions.filter((val) => val != recruiterAction.name);
          recruiterAction.nodes.forEach((node: IPipelineData) => {
            tempActions = tempActions.filter((val) => val != node.name);
            node.nodes.forEach((node2: IPipelineData) => {
              tempActions = tempActions.filter((val) => val != node2.name);
            });
          });
        }
      } else {
        value && tempActions.push(value);
        nextValue && tempActions.push(nextValue);
        nextNextVal && tempActions.push(nextNextVal);
      }
      tempActions = Array.from(new Set(tempActions));
      setActions([...tempActions]);
    } else if (field == 'results') {
      if (tempResults.includes(value)) {
        if (recruiterAction.name === value) {
          tempResults = tempResults.filter((val) => val != recruiterAction.name);
          recruiterAction.nodes.forEach((node: IPipelineData) => {
            tempResults = tempResults.filter((val) => val != node.name);
            node.nodes.forEach((node2: IPipelineData) => {
              tempResults = tempResults.filter((val) => val != node2.name);
            });
          });
        }
      } else {
        value && tempResults.push(value);
        nextValue && tempResults.push(nextValue);
        nextNextVal && tempResults.push(nextNextVal);
      }
      tempResults = Array.from(new Set(tempResults));
      setResults([...tempResults]);
    }
    onInputChange({
      actions: tempActions,
      results: tempResults,
    });
  };

  const generateRandomIdForTopNodes = () => {
    const node = String(gen.next().value);
    const oldTrackedNodes = [...expansionNodeTracker];
    if (!intializeState && !oldTrackedNodes.includes(node)) {
      oldTrackedNodes.push(node);
      setExpansionNodeTracker(oldTrackedNodes);
    }
    return node;
  };

  const generateActionAndResponse = (
    actionOrResponse: string,
    actionsOrResults: string[],
    field: string
  ) => (
    <TreeView
      expanded={expansionNodeTracker}
      aria-label="file system navigator"
      defaultCollapseIcon={<RemoveIcon />}
      defaultExpandIcon={<AddIcon />}
      onNodeToggle={(_, nodeIds: string[]) => {
        setIntializeState(true);
        setExpansionNodeTracker(nodeIds);
      }}
      sx={{ flexGrow: 1, overflowY: 'auto', mt: 2 }}
    >
      {JSON.parse(actionOrResponse).map((recruiterAction: IPipelineData, index: number) => (
        <TreeItem
          nodeId={generateRandomIdForTopNodes()}
          key={index}
          label={
            <FormControlLabel
              control={
                <Checkbox
                  onChange={() =>
                    setActionsOrResults(field, recruiterAction.name, '', '', recruiterAction)
                  }
                  checked={actionsOrResults.includes(recruiterAction.name) ? true : false}
                />
              }
              label={
                <WsTypography color={WsTypoColors.PRIMARY}>{recruiterAction.name}</WsTypography>
              }
            />
          }
        >
          {recruiterAction.nodes.map((recruiterAction1: IPipelineData, index1: number) => (
            <TreeItem
              nodeId={String(gen.next().value)}
              key={index1}
              label={
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={() =>
                        setActionsOrResults(
                          field,
                          recruiterAction1.name,
                          recruiterAction.name,
                          '',
                          recruiterAction1
                        )
                      }
                      checked={actionsOrResults.includes(recruiterAction1.name) ? true : false}
                    />
                  }
                  label={
                    <WsTypography color={WsTypoColors.PRIMARY}>
                      {recruiterAction1.name}
                    </WsTypography>
                  }
                />
              }
            >
              {recruiterAction1.nodes.map((recruiterAction2: IPipelineData, index2: number) => (
                <TreeItem
                  nodeId={String(gen.next().value)}
                  key={index2}
                  label={
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={() =>
                            setActionsOrResults(
                              field,
                              recruiterAction2.name,
                              recruiterAction1.name,
                              recruiterAction.name,
                              recruiterAction2
                            )
                          }
                          checked={actionsOrResults.includes(recruiterAction2.name) ? true : false}
                        />
                      }
                      label={
                        <WsTypography color={WsTypoColors.PRIMARY}>
                          {recruiterAction2.name}
                        </WsTypography>
                      }
                    />
                  }
                ></TreeItem>
              ))}
            </TreeItem>
          ))}
        </TreeItem>
      ))}
    </TreeView>
  );

  return (
    <Grid container>
      <Grid item xs={6}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
          Recruiter Actions
        </WsTypography>
        {generateActionAndResponse(
          candidateInteractionMessagingPipeline.pipelineData,
          actions,
          'actions'
        )}
      </Grid>
      <Grid item xs={6}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
          Candidate Response
        </WsTypography>
        {generateActionAndResponse(
          candidateInteractionResultPipeline.pipelineData,
          results,
          'results'
        )}
      </Grid>
    </Grid>
  );
};

export default RecruiterActivity;
